import { useCallback, useState } from 'react'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { isOptimizedForQuality } from '../leadFormType'

export const useAdCreativeProcessor = (refreshTrigger = 0) => {
    const { getStepContentData } = useWizard()
    const { connectedPublishers } = useMediaBuyingContext()

    const collectFormData = useCallback(() => {
        const selectFormatData = getStepContentData('selectFormat') || {}
        const setAdContentData = getStepContentData('setAdContent') || {}
        const uploadAssetsData = getStepContentData('uploadAssets') || {}
        const leadFormData = getStepContentData('leadForm') || {}
        const reviewSaveData = getStepContentData('reviewSave') || {}
        const setMediaCropData = getStepContentData('setMediaCrop') || {}

        // Determine lead form ID
        let leadFormId = null
        let metaFormId = null

        if (leadFormData.selectedForm) {
            // Using existing form
            leadFormId = leadFormData.selectedForm.id
            metaFormId = leadFormData.selectedForm.meta_form_id || leadFormData.selectedForm.id
        } else if (leadFormData.newFormId) {
            // Using newly created form
            leadFormId = leadFormData.newFormId
            metaFormId = leadFormData.newFormId
        }

        return {
            name: reviewSaveData.name || '',
            format: selectFormatData.format?.replace('singleImage', 'IMAGE').toUpperCase() || 'IMAGE',
            // has_lead_form: selectFormatData.showLeadForm || false, // Temporarily removed - column doesn't exist
            lead_form_id: leadFormId,
            meta_form_id: metaFormId,
            call_to_action_type: setAdContentData.cta || 'learn_more',
            call_to_action_url: setAdContentData.destinationUrl || '',
            call_to_action_text: setAdContentData.displayLink || '',
            primary_texts: Array.isArray(setAdContentData.primaryText)
                ? setAdContentData.primaryText
                : [setAdContentData.primaryText || ''],
            headlines: Array.isArray(setAdContentData.headline)
                ? setAdContentData.headline
                : [setAdContentData.headline || ''],
            descriptions: Array.isArray(setAdContentData.description)
                ? setAdContentData.description
                : [setAdContentData.description || ''],
            facebook_page_id: connectedPublishers ? Object.values(connectedPublishers)?.[0]?.page_id || '' : '',
            instagram_page_id: connectedPublishers ? Object.values(connectedPublishers)?.[0]?.instagram_id || '' : '',
            utm_parameters: null,
            tags: reviewSaveData.tags || [],
        }
    }, [getStepContentData, connectedPublishers])

    const createInstantForm = useCallback(() => {
        const leadFormData = getStepContentData('leadForm') || {}
        if (leadFormData.selectedForm && !leadFormData.editingFormId) {
            return null // Using existing form, no need to create
        }

        const formTypeData = getStepContentData('formType') || {}
        const formIntroData = getStepContentData('formIntro') || {}
        const formQuestionsData = getStepContentData('formQuestions') || {}
        const formPrivacyData = getStepContentData('formPrivacy') || {}
        const formThankYouData = getStepContentData('formThankYou') || {}

        // Build context card if form intro has content
        let contextCard = null
        if (formIntroData.hasGreeting && (formIntroData.headline || formIntroData.description)) {
            contextCard = {
                title: formIntroData.headline || '',
                style: 'PARAGRAPH_STYLE',
            }

            // Handle description based on type
            if (formIntroData.description) {
                if (
                    formIntroData.description.descriptionType === 'list' &&
                    formIntroData.description.list?.length > 0
                ) {
                    contextCard.content = formIntroData.description.list.filter((item) => item && item.trim())
                    contextCard.style = 'LIST_STYLE'
                } else if (
                    formIntroData.description.descriptionType === 'paragraph' &&
                    formIntroData.description.paragraph
                ) {
                    contextCard.content = [formIntroData.description.paragraph]
                }
            }
        }

        // Process questions to add Facebook API required fields
        const processedQuestions = (formQuestionsData.questions || [])
            .map((question) => {
                const processedQuestion = { ...question }
                const normalizedType = typeof question?.type === 'string' ? question.type : ''

                // Recover malformed legacy custom questions that have text/options but no type.
                const inferredType =
                    normalizedType ||
                    (question?.question || (Array.isArray(question?.options) && question.options.length > 0)
                        ? 'CUSTOM'
                        : '')
                if (!inferredType) return null
                processedQuestion.type = inferredType

                // Add inline_context for contact fields if contactDescription exists
                const contactFieldTypes = [
                    'EMAIL',
                    'PHONE',
                    'STREET_ADDRESS',
                    'CITY',
                    'STATE',
                    'PROVINCE',
                    'COUNTRY',
                    'POST_CODE',
                    'ZIP_CODE',
                ]
                if (contactFieldTypes.includes(processedQuestion.type) && formQuestionsData.contactDescription) {
                    processedQuestion.inline_context = formQuestionsData.contactDescription
                }

                // Handle custom questions
                if (processedQuestion.type === 'CUSTOM') {
                    // Set label from question text for custom questions
                    processedQuestion.label = question.question || 'Custom Question'

                    // For multiple choice, convert options array to Facebook format
                    // Facebook determines input type by presence/absence of options array
                    if (question.options && question.options.length > 0) {
                        processedQuestion.options = question.options
                            .filter((opt) => opt && opt.trim())
                            .map((opt) => ({
                                key: opt.toLowerCase().replace(/\s+/g, '_'),
                                value: opt,
                            }))
                    }
                    // No options = short answer text field (default behavior)
                }

                // Clean up properties for Facebook API compliance following best practices
                const cleanedQuestion = {
                    key: processedQuestion.key,
                    type: processedQuestion.type,
                }

                // Apply best practice rules based on question type
                if (processedQuestion.type === 'CUSTOM') {
                    // CUSTOM fields: Always include label
                    cleanedQuestion.label = processedQuestion.label

                    // Add options for multiple choice (presence determines if it's multiple choice vs text)
                    if (processedQuestion.options && processedQuestion.options.length > 0) {
                        cleanedQuestion.options = processedQuestion.options
                    }
                    // No options = short answer text field (Facebook's default behavior)
                }
                // System fields (EMAIL, FULL_NAME, etc.) - No labels needed, Facebook provides them

                // Add conditional properties for both types
                if (processedQuestion.inline_context) {
                    cleanedQuestion.inline_context = processedQuestion.inline_context
                }

                return cleanedQuestion
            })
            .filter(Boolean)

        const instantFormPayload = {
            name: formTypeData.formName || 'Untitled Form',
            is_optimized_for_quality: isOptimizedForQuality(formTypeData.formType),
            locale: 'en_US',
            privacy_policy: {
                url: formPrivacyData.privacyPolicyUrl || '',
                link_text: formPrivacyData.privacyPolicyLinkText || 'Privacy Policy',
            },
            // Only include follow_up_action_url if websiteUrl exists
            ...(formThankYouData.websiteUrl && {
                follow_up_action_url: formThankYouData.websiteUrl,
            }),
            thank_you_page: {
                title: formThankYouData.thankYouMessage || '',
                ...(formThankYouData.description && {
                    body: formThankYouData.description,
                }),
                button_type:
                    formThankYouData.websiteUrl || formThankYouData.websiteButtonText ? 'VIEW_WEBSITE' : 'NONE',
                ...(formThankYouData.websiteUrl && {
                    website_url: formThankYouData.websiteUrl,
                }),
                ...(formThankYouData.websiteButtonText && {
                    button_text: formThankYouData.websiteButtonText,
                }),
            },
            questions: processedQuestions,
        }

        // Add context card if it has content
        if (contextCard && (contextCard.title || contextCard.content?.length > 0)) {
            instantFormPayload.context_card = contextCard
        }

        return instantFormPayload
    }, [refreshTrigger])

    return {
        collectFormData,
        createInstantForm,
    }
}
